"use client";

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Root, RootContent, PhrasingContent } from "mdast";
import type { CoreReference } from "./use-core-conversation";

const referencePrefix = "#core-reference-";
const missingReference = "#core-unresolved-reference";

/** Providers sometimes copy a source title's literal pipe into a GFM table. */
function normalizeTableReferenceTitles(text: string, references: CoreReference[]) {
  const titles = [...new Set(references.map(reference => reference.title).filter(title => title.includes("|") && !title.includes("\n")))].sort((a, b) => b.length - a.length);
  if (!titles.length) return text;
  let fence: { character: string; length: number } | null = null;
  let table = false;
  return text.split("\n").map(line => {
    const marker = line.match(/^ {0,3}(`{3,}|~{3,})/);
    if (marker) {
      if (!fence) fence = { character: marker[1][0], length: marker[1].length };
      else if (marker[1][0] === fence.character && marker[1].length >= fence.length) fence = null;
      table = false;
      return line;
    }
    if (fence) return line;
    if (/^ {0,3}\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)+\|?\s*$/.test(line)) { table = true; return line; }
    if (!line.trim() || !line.includes("|")) table = false;
    if (!table) return line;
    // Exact, known titles only: never guess how arbitrary excess cells should align.
    return titles.reduce((row, title) => row.split(title).join(title.replaceAll("|", "\\|")), line);
  }).join("\n");
}

/** Resolve citations in prose only. Code examples and existing links stay literal. */
function remarkCoreReferences({ references }: { references: CoreReference[] }) {
  return (tree: Root) => {
    function visit(parent: { children: RootContent[] }) {
      const children: RootContent[] = [];
      for (const child of parent.children) {
        if (child.type === "text") {
          const pattern = /\[(source|signal|claim|system|simulation|projection|portfolio|world-model):([^\]\s]+)\]/g;
          let offset = 0;
          for (const match of child.value.matchAll(pattern)) {
            if (match.index! > offset) children.push({ type: "text", value: child.value.slice(offset, match.index) });
            const index = references.findIndex(reference => reference.kind === match[1] && (reference.id === match[2] || (reference.snapshotId && `${reference.snapshotId}/${reference.id}` === match[2])));
            children.push({ type: "link", url: index >= 0 ? `${referencePrefix}${index}` : missingReference,
              title: index >= 0 ? null : `Unresolved record: ${match[0]}`,
              children: [{ type: "text", value: index >= 0 ? references[index].title : `Unresolved ${match[1]}` }] });
            offset = match.index! + match[0].length;
          }
          if (offset === 0) children.push(child);
          else if (offset < child.value.length) children.push({ type: "text", value: child.value.slice(offset) });
        } else {
          if ("children" in child && child.type !== "link" && child.type !== "linkReference") visit(child);
          children.push(child);
        }
      }
      parent.children = children as PhrasingContent[];
    }
    visit(tree);
  };
}

export default function CoreResponse({ text, references = [], onOpenReference, pending = false }: {
  text: string;
  pending?: boolean;
  references?: CoreReference[];
  onOpenReference?: (reference: CoreReference) => void;
}) {
  return <div className="core-response">
    <Markdown remarkPlugins={[remarkGfm, [remarkCoreReferences, { references }]]} components={{
      // The page already owns its h1. Responses use a restrained section hierarchy.
      h1: ({ children }) => <h2>{children}</h2>,
      a: ({ href, children, title }) => {
        if (href?.startsWith(referencePrefix)) {
          const index = href.slice(referencePrefix.length);
          const reference = /^\d+$/.test(index) ? references[Number(index)] : undefined;
          if (reference) return onOpenReference
            ? <button type="button" className="core-inline-reference" aria-label={`Open source: ${reference.title}`} title={reference.title} onClick={() => onOpenReference(reference)}>[{Number(index) + 1}]</button>
            : <span>{reference.title}</span>;
        }
        if (href === missingReference) return <span className="core-unresolved-reference" title={title}>{children}</span>;
        if (!href) return <span>{children}</span>;
        return <a href={href} title={title} target={href.startsWith("#") ? undefined : "_blank"} rel="noopener noreferrer">{children}</a>;
      },
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- The overflow region must support keyboard scrolling of wide tables.
      table: ({ children }) => <div className="core-table-scroll" role="region" aria-label="Response table" tabIndex={0}><table>{children}</table></div>,
      // Model-supplied images are opened deliberately, not fetched as tracking pixels.
      img: ({ src, alt }) => typeof src === "string" && src
        ? <a href={src} target="_blank" rel="noopener noreferrer">{alt || "Open image"}</a>
        : <span>{alt || "Image unavailable"}</span>,
    }}>{normalizeTableReferenceTitles(pending ? text.replace(/\[(?:source|signal|claim|system|simulation|projection|portfolio|world-model):[^\]]*$/, "") : text, references)}</Markdown>
  </div>;
}
